// Building-scope 24 h / 96-slot cycle panel on Konfiguraatio, Datan laatu,
// Talotekniikan toimivuus, Sisäilmaolosuhteet, and Energiatehokkuus (stories
// #1612 / #1768). Own card — not the caption strip, not pulse chrome (no phase
// list, metric pills, legend, or "Viim. 24 h · 96 sykliä"). Konfiguraatio uses
// tick-derived slot colour; the four lens pages overlay amber/green from that
// page's Havainnot. The run line (Ajettu · seuraava) lives here. Slot click
// opens list-data detail (no second fetch) — see docs/spec/concepts.md § Six
// lenses and docs/spec/knowledge-base.md § Evaluation-run marker / § KB tick
// summary.
import { type ReactNode, useState, useEffect } from "react";
import {
  findingSourceLabelFact,
  sortFindings,
  SOURCE_TYPE_LABEL,
} from "@/components/building/findings/findings-helpers";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  getKBEvaluationRun,
  listKBTickSummaries,
  type KBEvaluationRun,
  type KBTickSummary,
} from "@/lib/api/agent";
import type { Finding } from "@/lib/api/building";
import { cardBase, cardSubtle } from "@/lib/card-styles";
import { ScopedLink } from "@/lib/scoped-nav";
import { cn } from "@/lib/utils";
import { KbRunIndicator } from "./KbRunIndicator";
import {
  cycleAxisLabels,
  cycleRibbonEndMs,
  formatDateFi,
  formatLastRunFi,
} from "./kb-run-timing";
import {
  applyLensSlotColouring,
  dayBreakSlotIndex,
  deriveCycleSlots,
  findingsOpenDuringSlot,
  type CycleSlot,
  type CycleSlotState,
} from "./kb-cycle-slots";
import type { LensFindingFilter } from "./lens-topic-metrics";

// The shared card-styles tones (ok/warn/empty/learning) don't cover a distinct sweep-failure
// "error" or the not-evaluated/no-sweep split this ribbon needs, so the five states map to
// existing theme utility classes directly rather than inventing new palette entries.
const SLOT_STATE_CLASS: Record<CycleSlotState, string> = {
  clean: "bg-success",
  findings: "bg-warning",
  error: "bg-destructive",
  "no-sweep": "bg-muted-foreground/50",
  "not-evaluated": "bg-muted",
};

const SLOT_STATE_LABEL: Record<CycleSlotState, string> = {
  clean: "Arvioitu, ei havaintoja",
  findings: "Havaintoja",
  error: "Virheitä arvioinnissa",
  "no-sweep": "Ei arvioitu tässä jaksossa",
  "not-evaluated": "Agentti ei ole arvioinut tätä jaksoa",
};

const QUIET_TOIMINTO_LINK_CLASS =
  "text-sm font-medium text-primary hover:underline";

/** Optional per-lens dialog body. Absent = today's mixed tick `<dl>` (Konfiguraatio). */
export type CycleSlotLensConfig = {
  filter: LensFindingFilter;
  findings: Finding[] | null;
};

type LoadState =
  | { kind: "loading" }
  | { kind: "error" }
  | { kind: "ready"; slots: CycleSlot[] };

export function KbCycleRibbon({
  buildingId,
  lens,
  cycle,
}: {
  buildingId: string;
  lens?: CycleSlotLensConfig;
  /**
   * When set (Energiatehokkuus building snapshot), skip evaluation-run + tick-list HTTP
   * and feed the run indicator the same ticks.
   */
  cycle?: {
    evaluationRun: KBEvaluationRun | null;
    ticks: KBTickSummary[];
  };
}) {
  const cycleFingerprint = cycle
    ? `${cycle.evaluationRun?.last_evaluated_at ?? ""}:${cycle.ticks.map((t) => t.tick_id).join(",")}`
    : null;
  const [state, setState] = useState<LoadState>(() =>
    cycle ? readySlotsFromCycle(buildingId, cycle) : { kind: "loading" },
  );

  useEffect(() => {
    if (cycleFingerprint != null && cycle) {
      setState(readySlotsFromCycle(buildingId, cycle));
      return;
    }
    let cancelled = false;
    setState({ kind: "loading" });
    Promise.all([
      getKBEvaluationRun(buildingId),
      listKBTickSummaries(buildingId),
    ])
      .then(([run, tickList]) => {
        if (cancelled) return;
        setState({
          kind: "ready",
          slots: deriveCycleSlots(
            run,
            tickList.ticks,
            cycleRibbonEndMs(run.last_evaluated_at),
          ),
        });
      })
      .catch(() => {
        if (!cancelled) setState({ kind: "error" });
      });
    return () => {
      cancelled = true;
    };
    // `cycle` is read when fingerprint matches; identity is intentionally excluded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildingId, cycleFingerprint]);

  return (
    <CycleRibbonPanel
      slots={slotsForLens(state, lens)}
      timing={<KbRunIndicator buildingId={buildingId} ticks={cycle?.ticks} />}
      lens={lens}
    />
  );
}

function readySlotsFromCycle(
  buildingId: string,
  cycle: {
    evaluationRun: KBEvaluationRun | null;
    ticks: KBTickSummary[];
  },
): LoadState {
  const run = cycle.evaluationRun ?? {
    building_id: buildingId,
    first_evaluated_at: null,
    last_evaluated_at: null,
  };
  return {
    kind: "ready",
    slots: deriveCycleSlots(
      run,
      cycle.ticks,
      cycleRibbonEndMs(run.last_evaluated_at),
    ),
  };
}

function slotsForLens(
  load: LoadState,
  lens?: CycleSlotLensConfig,
): CycleSlot[] | null {
  if (load.kind !== "ready") return null;
  if (!lens) return load.slots;
  if (lens.findings === null) return null;
  return applyLensSlotColouring(load.slots, lens.findings, lens.filter);
}

/** Pure panel chrome — timing + strip + hour axis. No fetching. */
export function CycleRibbonPanel({
  slots,
  timing,
  lens,
}: {
  slots: CycleSlot[] | null;
  timing: ReactNode;
  lens?: CycleSlotLensConfig;
}) {
  const breakIndex = slots ? dayBreakSlotIndex(slots) : null;
  const breakFraction =
    breakIndex !== null && slots ? breakIndex / slots.length : null;
  const axis =
    slots && slots.length > 0
      ? cycleAxisLabels(slots[0].start, slots[slots.length - 1].end)
      : [];

  return (
    <div className={cn(cardBase, "p-4")}>
      <div className="mb-3">{timing}</div>
      {slots ? (
        <div className="relative">
          <CycleRibbonStrip slots={slots} lens={lens} />
          {breakFraction !== null ? (
            <div
              className="pointer-events-none absolute -top-1 -bottom-1 w-0.5 rounded-full bg-foreground/90 ring-2 ring-background"
              style={{ left: `${breakFraction * 100}%` }}
              aria-hidden
            />
          ) : null}
        </div>
      ) : (
        <div
          className="h-6 rounded-md border border-border bg-muted"
          aria-hidden
        />
      )}
      {slots && slots.length > 0 ? (
        <div className="mt-1 flex justify-between font-mono text-[9px] text-muted-foreground">
          {axis.map((label, i) => (
            <span key={`${label}-${i}`}>{label}</span>
          ))}
        </div>
      ) : null}
      {breakFraction !== null && breakIndex !== null && slots ? (
        <div className="relative h-3">
          <span
            className="absolute translate-x-1 whitespace-nowrap font-mono text-[9px] text-foreground/60"
            style={{ left: `${breakFraction * 100}%` }}
          >
            {formatDateFi(slots[breakIndex].start)}
          </span>
        </div>
      ) : null}
    </div>
  );
}

/** Pure rendering of already-derived slots — no fetching. Click opens the list-data dialog. */
export function CycleRibbonStrip({
  slots,
  lens,
}: {
  slots: CycleSlot[];
  lens?: CycleSlotLensConfig;
}) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const openSlot = openIndex === null ? null : slots[openIndex];

  return (
    <>
      <div
        className="flex h-6 gap-[2px] overflow-hidden rounded-md border border-border bg-background p-[2px]"
        role="img"
        aria-label="Arviointisyklien 24 tunnin historia"
      >
        {slots.map((slot, index) => (
          <button
            type="button"
            key={index}
            className={cn(
              "h-full flex-1 rounded-[2px]",
              SLOT_STATE_CLASS[slot.state],
            )}
            aria-label={SLOT_STATE_LABEL[slot.state]}
            onClick={() => setOpenIndex(index)}
          />
        ))}
      </div>
      <Dialog
        open={openSlot !== null}
        onOpenChange={(open) => {
          if (!open) setOpenIndex(null);
        }}
      >
        {openSlot ? (
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Arviointisykli</DialogTitle>
            </DialogHeader>
            <CycleSlotDetail slot={openSlot} lens={lens} />
          </DialogContent>
        ) : null}
      </Dialog>
    </>
  );
}

/** Headline of one slot — list data only, never a fabricated run. */
export function CycleSlotDetail({
  slot,
  lens,
}: {
  slot: CycleSlot;
  lens?: CycleSlotLensConfig;
}) {
  return (
    <div className="space-y-3 text-sm">
      <div>
        <div className="text-foreground">{SLOT_STATE_LABEL[slot.state]}</div>
        <div className="mt-0.5 font-mono text-[11px] text-muted-foreground tabular-nums">
          {formatLastRunFi(slot.start)} – {formatLastRunFi(slot.end)}
        </div>
      </div>
      {lens ? (
        <LensSlotBody slot={slot} lens={lens} />
      ) : slot.tick ? (
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
          <DetailRow
            label="Ajettu"
            value={`${formatLastRunFi(slot.tick.started_at)} – ${formatLastRunFi(slot.tick.finished_at)}`}
          />
          <DetailRow
            label="Laitteet"
            value={String(slot.tick.equipment_evaluated)}
          />
          <DetailRow
            label="Vyöhykkeet"
            value={String(slot.tick.zones_evaluated)}
          />
          <DetailRow
            label="Kirjoitetut pisteet"
            value={String(slot.tick.data_points_written)}
          />
          <DetailRow
            label="Havaintoja"
            value={String(slot.tick.behaviours_failing)}
          />
          <DetailRow label="Virheet" value={String(slot.tick.errors)} />
        </dl>
      ) : (
        <p className="text-xs text-muted-foreground">
          Ei tallennettua ajoa tälle jaksolle.
        </p>
      )}
    </div>
  );
}

function LensSlotBody({
  slot,
  lens,
}: {
  slot: CycleSlot;
  lens: CycleSlotLensConfig;
}) {
  if (slot.state === "clean") {
    return (
      <p className="text-xs text-muted-foreground">
        Ei toimittavaa tällä näkymällä.
      </p>
    );
  }

  if (slot.state === "findings") {
    if (lens.findings === null) {
      return (
        <p className="text-xs text-muted-foreground">Ladataan havaintoja…</p>
      );
    }
    const inSlot = findingsOpenDuringSlot(lens.findings, slot, lens.filter);
    return (
      <div className="space-y-3">
        {inSlot.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            Ei tämän näkymän havaintoja tällä jaksolla.
          </p>
        ) : (
          <ul className="space-y-2">
            {sortFindings(inSlot).map((finding) => (
              <li key={finding.id}>
                <SlotHavaintoDetail finding={finding} filter={lens.filter} />
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  if (slot.state === "error") {
    return (
      <div className="space-y-3">
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
          <DetailRow label="Virheet" value={String(slot.tick?.errors ?? 0)} />
        </dl>
        <ScopedLink to="/activity" className={QUIET_TOIMINTO_LINK_CLASS}>
          Näytä Tapahtumalokissa
        </ScopedLink>
      </div>
    );
  }

  return (
    <p className="text-xs text-muted-foreground">
      Ei tallennettua ajoa tälle jaksolle.
    </p>
  );
}

function SlotHavaintoDetail({
  finding,
  filter,
}: {
  finding: Finding;
  filter: LensFindingFilter;
}) {
  const sourceLabel = findingSourceLabelFact(finding);
  const where = sourceLabel
    ? `${SOURCE_TYPE_LABEL[finding.source_type]} · ${sourceLabel}`
    : SOURCE_TYPE_LABEL[finding.source_type];
  return (
    <div className={cn(cardSubtle, "p-3 space-y-2")}>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
        Havainto
      </div>
      <div className="text-xs font-medium leading-snug">
        {finding.observation}
      </div>
      <div className="text-[11px] text-muted-foreground">{where}</div>
      <ScopedLink
        to="/findings"
        search={(prev) => ({
          ...prev,
          category: filter.category,
          source: filter.sourceType,
          finding: finding.id,
        })}
        className={QUIET_TOIMINTO_LINK_CLASS}
      >
        Näytä Havainnoissa
      </ScopedLink>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-right font-mono tabular-nums">{value}</dd>
    </>
  );
}
